/** @jsxImportSource @emotion/react */
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { clientConfigAtom } from '@app/state/clientsAtom'
import axios from 'axios'
import { useAtom, useAtomValue } from 'jotai'
import { DatabaseIcon } from 'lucide-react'
import * as React from 'react'
import {
  columnFiltersAtomFamily,
  columnVisibilityAtomFamily,
  globalFilterAtomFamily,
  sortingAtomFamily,
} from '../state/dataLayerTableUiState.atoms'
import { groupByHazardName, tabAtomMap } from '../utils/data.submodule.utils'
import { DataModuleRow } from '../types'
import { columns } from './DataLayerTable.columns'
import * as styles from './DataLayerTable.styles'
import { ExportDataTable } from './DataLayerTableExport'

type TabAtomKey = keyof typeof tabAtomMap

// Collapses hazard rows sharing the same hazardName into a single representative
// row (per useHazardTableRows.ts grouping), displayed with hazardName as its name.
const collapseHazardGroups = (rows: DataModuleRow[]): DataModuleRow[] => {
  const hazardRows = rows.filter((r) => r.label === 'Hazard')
  const otherRows = rows.filter((r) => r.label !== 'Hazard')

  const grouped = groupByHazardName(hazardRows)
  const collapsed = Array.from(grouped.entries()).map(([hazardName, groupRows]) => ({
    ...groupRows[0],
    name: hazardName,
  }))

  return [...otherRows, ...collapsed]
}

export function DataLayerTable({ activeTab }: { activeTab: TabAtomKey }) {
  const allRows = useAtomValue(tabAtomMap[activeTab])
  const clientConfig = useAtomValue(clientConfigAtom)
  const [sorting, setSorting] = useAtom(sortingAtomFamily(activeTab))
  const [globalFilter, setGlobalFilter] = useAtom(globalFilterAtomFamily(activeTab))
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtomFamily(activeTab))
  const [columnVisibility, setColumnVisibility] = useAtom(columnVisibilityAtomFamily(activeTab))

  const [columnsMenuAnchor, setColumnsMenuAnchor] = React.useState<null | HTMLElement>(null)
  const isColumnsMenuOpen = Boolean(columnsMenuAnchor)

  const handleOpenColumnsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget)
  }

  const handleCloseColumnsMenu = () => {
    setColumnsMenuAnchor(null)
  }

  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null)
  const isExportMenuOpen = Boolean(exportMenuAnchor)

  const handleOpenExportMenu = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget)
  }

  const handleCloseExportMenu = () => {
    setExportMenuAnchor(null)
  }

  const handleDownloadFile = async (url: string) => {
    handleCloseExportMenu()

    const response = await axios.get(url, { responseType: 'blob' })
    const filename = decodeURIComponent(url.split('/').pop() ?? '')

    const link = document.createElement('a')
    const blobUrl = URL.createObjectURL(response.data)
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  }

  const dataModuleDownloads = clientConfig?.data_module_downloads
  const hasDataModuleDownloads =
    !!dataModuleDownloads && Object.keys(dataModuleDownloads).length > 0

  const exportMenuItems = hasDataModuleDownloads
    ? [
        ['Export CSV', null] as [string, null],
        ...Object.entries(dataModuleDownloads).sort(([a], [b]) => a.localeCompare(b)),
      ]
    : []

  const headerRef = React.useRef<HTMLDivElement>(null)
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const isSyncingRef = React.useRef(false)

  // Sync horizontal scroll between the fixed header and scrolling body
  React.useEffect(() => {
    const header = headerRef.current
    const body = bodyRef.current
    if (!header || !body) return

    const onHeaderScroll = () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      body.scrollLeft = header.scrollLeft
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    }

    const onBodyScroll = () => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      header.scrollLeft = body.scrollLeft
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    }

    header.addEventListener('scroll', onHeaderScroll, { passive: true })
    body.addEventListener('scroll', onBodyScroll, { passive: true })

    return () => {
      header.removeEventListener('scroll', onHeaderScroll)
      body.removeEventListener('scroll', onBodyScroll)
    }
  }, [])

  const groupHazards = activeTab === 'hazards' || activeTab === 'all'

  const rows = React.useMemo(() => {
    if (!allRows) return allRows
    return groupHazards ? collapseHazardGroups(allRows) : allRows
  }, [allRows, groupHazards])

  const table = useReactTable({
    data: rows || [],
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    enableSortingRemoval: false,
  })

  const exportColumns = table
    .getVisibleLeafColumns()
    .filter((column) => column.id !== 'actions')
    .map((column) => ({
      id: column.id,
      header: typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id,
    }))

  const exportRows = table.getFilteredRowModel().rows.map((row) => {
    const rowValues = row.getAllCells().reduce<Record<string, unknown>>((acc, cell) => {
      acc[cell.column.id] = cell.getValue()
      return acc
    }, {})

    return exportColumns.reduce<Record<string, unknown>>((acc, column) => {
      acc[column.id] = rowValues[column.id]
      return acc
    }, {})
  })

  return (
    <Paper css={styles.tableWrapper}>
      {/* ── Toolbar ── */}
      <Box css={styles.toolbar}>
        <Box css={styles.toolbarLeft}>
          <TextField
            size="small"
            placeholder="Search elements"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            css={styles.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" css={styles.categoryFilter}>
            <TextField
              select
              size="small"
              value={(table.getColumn('category')?.getFilterValue() as string) ?? ''}
              onChange={(e) => {
                const value = e.target.value
                table.getColumn('category')?.setFilterValue(value === '' ? undefined : value)
              }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Filter by category</em>
              </MenuItem>
              {Array.from(new Set(rows.map((r) => r.category)))
                .sort((a, b) => a.localeCompare(b))
                .map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>
        </Box>

        <ExportDataTable
          columns={exportColumns}
          rows={exportRows}
          fileName={`risk-data-${activeTab}.csv`}
        >
          {({ onClick, disabled }) =>
            hasDataModuleDownloads ? (
              <Box>
                <Button
                  variant={'contained'}
                  onClick={handleOpenExportMenu}
                  disabled={disabled}
                  size={'small'}
                >
                  Export
                </Button>

                <Menu
                  anchorEl={exportMenuAnchor}
                  open={isExportMenuOpen}
                  onClose={handleCloseExportMenu}
                  MenuListProps={{ dense: true }}
                >
                  {exportMenuItems.map(([label, url]) => (
                    <MenuItem
                      key={label}
                      onClick={() => {
                        if (url === null) {
                          onClick()
                          handleCloseExportMenu()
                        } else {
                          handleDownloadFile(url)
                        }
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <Button variant={'contained'} onClick={onClick} disabled={disabled} size={'small'}>
                Export CSV
              </Button>
            )
          }
        </ExportDataTable>
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={handleOpenColumnsMenu}
          >
            Columns
          </Button>

          <Menu
            anchorEl={columnsMenuAnchor}
            open={isColumnsMenuOpen}
            onClose={handleCloseColumnsMenu}
            MenuListProps={{ dense: true }}
          >
            {table
              .getAllLeafColumns()
              .filter((c) => c.getCanHide() && c.id !== 'actions')
              .map((column) => (
                <MenuItem
                  key={column.id}
                  onClick={() => column.toggleVisibility(!column.getIsVisible())}
                >
                  <Checkbox size="small" checked={column.getIsVisible()} />
                  <ListItemText
                    primary={
                      typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id
                    }
                  />
                </MenuItem>
              ))}
          </Menu>
        </Box>
      </Box>

      {/* ── Table ── */}
      <Box css={styles.tableWrapperBordered}>
        {/* Fixed Header */}
        <Box ref={headerRef} css={styles.tableHeaderScroller}>
          <Table aria-label="data table" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort()
                    const sortDir = header.column.getIsSorted()
                    const active = sortDir !== false
                    const direction: 'asc' | 'desc' = sortDir === 'desc' ? 'desc' : 'asc'

                    return (
                      <TableCell
                        key={header.id}
                        align={header.column.id === 'actions' ? 'right' : 'left'}
                        css={styles.headerCell}
                        sortDirection={active ? direction : false}
                        style={{ width: header.getSize(), minWidth: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : canSort ? (
                          <TableSortLabel
                            active={active}
                            direction={direction}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TableSortLabel>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableHead>
          </Table>
        </Box>

        {/* Scrollable Body */}
        <Box ref={bodyRef} css={styles.tableBodyScroller}>
          <Table aria-label="data table" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow hover tabIndex={-1} key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      align={cell.column.id === 'actions' ? 'right' : 'left'}
                      sx={{ '& a': { maxWidth: '100%' } }}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {table.getRowModel().rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} css={styles.emptyRow}>
                    <EmptyData title="No results found" icon={<DatabaseIcon />} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Paper>
  )
}
